import { Box, CircularProgress, Collapse, Typography } from '@mui/material';
import { IArgumentMetadata } from '@tymely/atoms';
import { IGrPolicy, IGrPolicyArgumentNode } from '@tymely/atoms/policy.atoms';
import { usePolicyVersions } from '@tymely/pages/GraphPolicyEditor/hooks/usePolicyVersions';
import { useArgumentsMetadataQuery, useFetchResponseQuery, usePolicyByVersionId, useTicket } from '@tymely/services';
import { memo, useCallback, useMemo, useState } from 'react';
import { PRE_FILTER_POLICY_SET_ID } from '@tymely/utils/constants';

import { TEMPLATE_METADATA_ID } from '../../constants';
import { ActionExecution, ConditionData, PreFilterEventProps } from '../types';
import { ConditionItem } from './ConditionItem';
import { ConditionsContainer, PreFilterContainer, ScenarioPromptTag } from './PreFilterEvent.styles';
import { FollowUpPromptButton, FollowUpPromptBox } from './FollowUpEvent.styles';

export const PreFilterEvent = memo(({ eventData, isExpanded }: PreFilterEventProps) => {
    const ticket = useTicket();
    const [showPromptOverride, setShowPromptOverride] = useState<number | null>(null);
    const [showScenarioPrompt, setShowScenarioPrompt] = useState(false);

    const { resolution, workflow_id, scenario } = eventData.decision;
    const templateId = useMemo(() => {
        const actionExecution = eventData.decision?.action_executions?.find(
            (ae: ActionExecution) =>
                ae.action_metadata_id === TEMPLATE_METADATA_ID || ae.action_metadata?.id === TEMPLATE_METADATA_ID,
        );
        return actionExecution?.additional_data?.args_template?.template_id;
    }, [eventData.decision?.action_executions]);

    const { data: templateData, isLoading: isTemplateLoading } = useFetchResponseQuery(templateId);

    const versionsQuery = usePolicyVersions(ticket?.organization_id ?? 0, PRE_FILTER_POLICY_SET_ID);
    const versions = versionsQuery.data || [];
    const eventCreatedDate = eventData?.created_date || eventData?.decision?.created_date;

    const selectedVersion = useMemo(() => {
        if (!eventCreatedDate || !versions.length) return undefined;
        const sorted = [...versions].sort(
            (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime(),
        );
        return sorted.find((v) => new Date(v.created_at) <= new Date(eventCreatedDate));
    }, [versions, eventCreatedDate]);

    const policyQuery = usePolicyByVersionId(selectedVersion?.policy_id);
    const policy = policyQuery.data as unknown as IGrPolicy;

    const conditionNodeData = useMemo(() => {
        if (!policy?.graph?.nodes || !eventData.decision?.decision_lineage) return [];
        return eventData.decision.decision_lineage.map((nodeId: string, idx: number) => {
            const node = policy.graph.nodes.find((n) => n.id === nodeId);
            const arg_metadata_id = (node as IGrPolicyArgumentNode)?.arg_metadata_id;
            return {
                index: idx,
                nodeId,
                arg_metadata_id,
            };
        });
    }, [policy, eventData.decision?.decision_lineage]);

    const { data: allArgumentMetadata, isLoading: isArgsLoading } = useArgumentsMetadataQuery();
    const argumentMetadataMap = useMemo(() => {
        if (!allArgumentMetadata) return new Map<number, IArgumentMetadata>();
        const ids = conditionNodeData.map((c: { arg_metadata_id?: number }) => c.arg_metadata_id).filter(Boolean);
        const filtered = allArgumentMetadata.filter((arg) => ids.includes(arg.id));
        return new Map<number, IArgumentMetadata>(filtered.map((arg) => [arg.id, arg]));
    }, [allArgumentMetadata, conditionNodeData]);

    const handlePromptOverrideToggle = useCallback((idx: number | null) => {
        setShowPromptOverride(idx);
    }, []);

    const conditionData = useMemo((): ConditionData[] => {
        if (!eventData.decision?.condition_lineage) return [];
        return eventData.decision.condition_lineage.map((condition: string, idx: number) => {
            const nodeInfo = conditionNodeData[idx];
            return {
                condition,
                isMet: condition.includes('condition met'),
                key: `${condition}-${idx}`,
                index: idx,
                arg_metadata_id: nodeInfo?.arg_metadata_id,
            };
        });
    }, [eventData.decision?.condition_lineage, conditionNodeData]);

    return (
        <PreFilterContainer>
            {eventData.decision.condition_lineage && (
                <Collapse in={isExpanded}>
                    <ConditionsContainer>
                        {versionsQuery.isLoading ||
                            policyQuery.isLoading ||
                            (isArgsLoading && <CircularProgress size={20} />)}
                        {!!scenario && (
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                <Typography variant="body2" color="text.secondary">
                                    Scenario: <b>{scenario}</b>
                                </Typography>
                            </Box>
                        )}
                        {conditionData.map(({ condition, isMet, key, index, arg_metadata_id }) => (
                            <ConditionItem
                                key={key}
                                condition={condition}
                                isMet={isMet}
                                argumentMetadataMap={argumentMetadataMap}
                                showPromptOverride={showPromptOverride}
                                setShowPromptOverride={handlePromptOverrideToggle}
                                index={index}
                                arg_metadata_id={arg_metadata_id}
                            />
                        ))}
                        {!!resolution && (
                            <ScenarioPromptTag
                                component="span"
                                sx={{
                                    textDecoration: 'none',
                                    cursor: 'default',
                                    ml: 1.7,
                                    mr: 1,
                                    mb: 1,
                                }}
                            >
                                {resolution}
                            </ScenarioPromptTag>
                        )}
                        {!!workflow_id && (
                            <Typography variant="body2" color="text.secondary">
                                Workflow ID: <b>{workflow_id}</b>
                            </Typography>
                        )}
                    </ConditionsContainer>
                    {templateId && (
                        <Box>
                            <FollowUpPromptButton
                                role="button"
                                tabIndex={0}
                                onClick={() => setShowScenarioPrompt(!showScenarioPrompt)}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter' || e.key === ' ') {
                                        setShowScenarioPrompt(!showScenarioPrompt);
                                    }
                                }}
                            >
                                Respond and resolve prompt
                            </FollowUpPromptButton>
                            <Collapse in={showScenarioPrompt}>
                                {isTemplateLoading ? (
                                    <Box sx={{ mt: 1.25 }}>
                                        <CircularProgress size={20} />
                                    </Box>
                                ) : (
                                    <FollowUpPromptBox>
                                        <Typography variant="body2" color="text.secondary">
                                            {templateData?.response_txt}
                                        </Typography>
                                    </FollowUpPromptBox>
                                )}
                            </Collapse>
                        </Box>
                    )}
                </Collapse>
            )}
        </PreFilterContainer>
    );
});

PreFilterEvent.displayName = 'PreFilterEvent';
